import React, { useEffect } from 'react'
import { Trans, t } from '@lingui/macro'
import { useRouteMatch } from 'react-router-dom'

import { RelativeCampaignActionWarning } from '../RelativeCampaignActionWarning'
import IconCalendar from '../../../../components/common/icon/IconCalendar'
import IconArrowLeft from '../../../../components/common/icon/IconArrowLeft'

import { ButtonPrimary, ButtonLight, Box } from 'front-end/src/components'
import NewActionHeader from '../new-action-header/NewActionHeader'
import ActionScheduleChoice from '../action-schedule-choice/ActionScheduleChoice'
import DateTimePickers from '../../../../components/common/date-time-pickers/DateTimePickers'
import SelectTimezone from '../../../../components/common/SelectTimezone'
import { ICampaignEmailComponentState } from '../campaign-email-component/CampaignEmailComponent'
import './CampaignActionScheduleStepComponent.less'
import IconExclamation from '../../../../components/common/icon/IconExclamation'

import { translateActionType } from '../../campaign-overview/create-campaign-action-modal/CreateCampaignActionModal'

import { CampaignScheduleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import {
  useCheckSmartScheduleCampaignActionQuery,
  useOrganisationBusinessHoursEnabledQuery
} from './CampaignActionScheduleStepComponent.codegen'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import { brandColors } from 'shared/basicPalette'
import { Dayjs } from 'shared/dayjs'
import Input from '../../../../components/common/form/input/Input'
import IconInfo from '../../../../components/common/icon/IconInfo'
import TimePicker from '../../../../components/common/date-time-pickers/TimePicker'

import { checkIsValidFutureDate } from 'front-end/src/utils/dateHelpers'
import IconSmartScheduling from 'public/assets/img/smart-scheduling.svg'
import { RespectBusinessHoursSwitchPanel } from '../RespectBusinessHoursSwitchPanel'
import { CampaignActionToggleRow } from '../CampaignActionToggleRow'
import { Text } from '@chakra-ui/react'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'

export interface ICampaignActionScheduleState
  extends Partial<ICampaignEmailComponentState> {
  relativeSchedule: {
    day: number | null
    time: string | null
  }
  fixedSchedule: { date: Dayjs | null }
}

function convert24hTo12h(time) {
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time
  ]

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1) // Remove full string match value
    time[5] = +time[0] < 12 ? ' AM' : ' PM' // Set AM/PM
    time[0] = +time[0] % 12 || 12 // Adjust hours
  }
  return time.join('') // return adjusted time or original string
}

export const CampaignActionScheduleStepComponent = (
  props: ICampaignActionScheduleState & {
    onStateUpdate: (updatedState: Partial<ICampaignActionScheduleState>) => void
    onSave: () => void
    actionType: CampaignActionTypeEnum
    steps: Record<string, unknown>
  }
) => {
  const { data } = useOrganisationBusinessHoursEnabledQuery()

  const matches = useRouteMatch<{ campaignId: string }>('/campaign/:campaignId')
  const matchesInActionEdit = useRouteMatch<{
    campaignId: string
    actionId: string
  }>('/campaign/:campaignId/:actionType/:actionId')
  const { relativeSchedule } = props

  const { data: smartScheduleAbilityData } =
    useCheckSmartScheduleCampaignActionQuery({
      skip: props.scheduleType === CampaignScheduleEnum.IMMEDIATE,
      variables: {
        campaignId: Number(matches?.params.campaignId),
        day: relativeSchedule.day,
        sendTime:
          props.fixedSchedule.date && props.fixedSchedule.date.isValid()
            ? props.fixedSchedule.date.toISOString()
            : null
      }
    })

  const actionId = Number(matchesInActionEdit?.params?.actionId)

  let canSmartSchedule = true
  if (smartScheduleAbilityData?.admin?.campaign?.smartScheduledActionFor?.id) {
    canSmartSchedule =
      smartScheduleAbilityData?.admin?.campaign?.smartScheduledActionFor?.id ===
      actionId
  }

  useEffect(() => {
    if (canSmartSchedule === false && props.automatic_time_of_day === true) {
      props.onStateUpdate({
        automatic_time_of_day: false // turn it off when user changes the time to when this cannot be enabled
      })
    }
  }, [canSmartSchedule])

  const isRelativeChoiceValid =
    relativeSchedule.day !== null &&
    !!(relativeSchedule.day >= 0 && relativeSchedule.time)

  const isFixedChoiceValid = checkIsValidFutureDate(
    props.fixedSchedule.date,
    props.timezone
  )

  const actionType = props.actionType

  const isFixedConfirmBtnDisabled = props.isSaving || !isFixedChoiceValid
  const isRelativeConfirmBtnDisabled = props.isSaving || !isRelativeChoiceValid

  return (
    <>
      <NewActionHeader
        title={translateActionType(actionType)}
        steps={props.steps}
        // @ts-expect-error 9/11/2020
        currentStep={
          props.steps[
            Object.keys(props.steps)[Object.keys(props.steps).length - 1] // Last step in steps is current
          ]
        }
        prevButton={
          <ButtonLight
            onClick={() => {
              props.onStateUpdate({
                // @ts-expect-error 9/11/2020
                step: props.steps[
                  Object.keys(props.steps)[
                    Object.keys(props.steps).length - 2 // -2 means previous step because -1 is current step
                  ]
                ]
              })
            }}
            leftIcon={<IconArrowLeft />}
          >
            <Trans>Back</Trans>
          </ButtonLight>
        }
      />

      <div className="ActionSchedule">
        {props.scheduleType === CampaignScheduleEnum.RELATIVE && (
          <ActionScheduleChoice
            className={CampaignScheduleEnum.RELATIVE}
            selected
          >
            <h2>
              <IconCalendar />
              <Trans>Relative schedule</Trans>
            </h2>
            <section
              css={{ width: '100%', alignItems: 'top', display: 'flex' }}
            >
              <Trans>
                Schedule your action to execute according to how long a person
                has been part of your campaign.
              </Trans>
            </section>
            <SectionHorizontalDivider />
            <section
              css={{
                width: '100%',
                alignItems: 'center',
                display: 'flex',
                gap: '5px'
              }}
            >
              <Text color={brandColors.black}>
                <Trans>Send on day</Trans>
              </Text>
              <Input
                type="number"
                className="ActionScheduleChoice__day"
                min="0"
                value={props.relativeSchedule.day ?? undefined}
                onChange={(val) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...props.relativeSchedule,
                      day: +val
                    }
                  })
                }}
              />
              <Text color={brandColors.black}>
                <Trans>at</Trans>
              </Text>
              <TimePicker
                placeholder={t`Time`}
                mr="8px"
                value={
                  relativeSchedule.time
                    ? relativeSchedule.time.substr(0, 5)
                    : ''
                }
                onChangeHandler={(val) => {
                  props.onStateUpdate({
                    relativeSchedule: {
                      ...relativeSchedule,
                      time: `${val}:00`
                    }
                  })
                }}
              />
              <Tooltip
                text={
                  <Trans>
                    Day 0 is the same day the user joins the campaign.
                    <br />
                    <br />
                    Timezone of the user will be taken into consideration. If
                    the user does not have a timezone set, your organisation
                    timezone will be used.
                  </Trans>
                }
              >
                <IconInfo />
              </Tooltip>
              <br />
            </section>
            <SectionHorizontalDivider />
            <CampaignActionToggleRow
              title={t`Smart scheduling`}
              icon={IconSmartScheduling}
              description={
                <>
                  <Trans>
                    Let Looop decide the best time of day to send this action
                    for each person, based on their data profile.
                  </Trans>{' '}
                  {relativeSchedule.time?.length === 8 && (
                    <span>
                      <Trans>
                        {convert24hTo12h(relativeSchedule.time.substr(0, 5))}{' '}
                        will be used as a fallback if the user has no past
                        actions.
                      </Trans>
                    </span>
                  )}
                </>
              }
              checked={!!props.automatic_time_of_day}
              onChange={async () => {
                props.onStateUpdate({
                  automatic_time_of_day: !props.automatic_time_of_day,
                  respect_business_hours: !props.automatic_time_of_day
                    ? true
                    : props.respect_business_hours
                })
              }}
              disabled={canSmartSchedule === false}
            />
            <RespectBusinessHoursSwitchPanel
              respectBusinessHours={props.respect_business_hours}
              onStateUpdate={props.onStateUpdate}
              businessHoursEnabled={
                data?.admin?.organisation?.businessHours.enabled
              }
            />
            <RelativeCampaignActionWarning day={relativeSchedule.day} />
            <Box display="flex" justifyContent="center">
              <ButtonPrimary
                onClick={() => {
                  if (props.isSaving) return

                  props.onStateUpdate({
                    scheduleType: CampaignScheduleEnum.RELATIVE,
                    fixedSchedule: { date: null }
                  })
                  props.onSave()
                }}
                isLoading={props.isSaving}
                disabled={isRelativeConfirmBtnDisabled}
              >
                <Trans>Confirm schedule and save</Trans>
              </ButtonPrimary>
            </Box>
          </ActionScheduleChoice>
        )}

        {props.scheduleType === CampaignScheduleEnum.FIXED && (
          <ActionScheduleChoice className={CampaignScheduleEnum.FIXED} selected>
            <h2>
              <IconCalendar />
              <Trans>Fixed schedule</Trans>
            </h2>
            <section>
              <Trans>
                Schedule your action to execute on a specific date, to everyone
                in your campaign audience.
              </Trans>
            </section>
            <section className="ActionScheduleChoice__set-date-input-container">
              <DateTimePickers
                variant="light"
                fullWidth
                datePlaceholder={t`Day`}
                timePlaceholder={t`Time`}
                label={t`Send message on`}
                labelStyle={{ minWidth: '100%' }}
                isUtc
                disablePastDays
                value={props.fixedSchedule.date}
                onChange={(date: Dayjs | null) => {
                  props.onStateUpdate({
                    fixedSchedule: { date }
                  })
                }}
              />
            </section>
            <section className="ActionScheduleChoice__set-date-input-container">
              <SelectTimezone
                value={props.timezone}
                guess
                variant="light"
                onChange={(tz) => {
                  props.onStateUpdate({
                    timezone: tz
                  })
                }}
              />
            </section>
            <Tooltip
              text={
                !isFixedChoiceValid &&
                t`Please set send time at least 3 minutes to the future`
              }
            >
              <Box display="flex" justifyContent="center">
                <ButtonPrimary
                  onClick={() => {
                    if (props.isSaving) return

                    props.onStateUpdate({
                      scheduleType: CampaignScheduleEnum.FIXED,
                      relativeSchedule: { day: 1, time: '10:00:00' } // the default value for a relative timed action
                    })
                    props.onSave()
                  }}
                  isLoading={props.isSaving}
                  disabled={isFixedConfirmBtnDisabled}
                >
                  <Trans>Confirm schedule and save</Trans>
                </ButtonPrimary>
              </Box>
            </Tooltip>
          </ActionScheduleChoice>
        )}

        {props.scheduleType === CampaignScheduleEnum.IMMEDIATE && (
          <ActionScheduleChoice className={CampaignScheduleEnum.FIXED} selected>
            <h2>
              <IconExclamation />
              <Trans>Immediate send</Trans>
            </h2>
            <section>
              <Trans>
                This action will be processed straight away after activating
                this campaign.
              </Trans>
            </section>
            <Box display="flex" justifyContent="center">
              <ButtonPrimary
                onClick={() => {
                  props.onStateUpdate({
                    relativeSchedule: { day: 1, time: '10:00:00' }, // the default value for a relative timed action
                    fixedSchedule: { date: null }
                  })
                  props.onSave()
                }}
                isLoading={props.isSaving}
              >
                <Trans>Confirm schedule and save</Trans>
              </ButtonPrimary>
            </Box>
          </ActionScheduleChoice>
        )}
      </div>
    </>
  )
}

const SectionHorizontalDivider = () => (
  <Box w={'100%'} h={'2px'} backgroundColor={brandColors.grey.lighter} />
)
